import React, { useState, useEffect, useRef } from 'react';
import { FaDownload, FaRedo, FaPlus, FaTrash, FaFileInvoice, FaBuilding, FaUser } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateInvoicePDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { applyMobileInputHints } from '../utils/formEnhancements';

const InvoiceGenerator = () => {
  // Invoice Settings
  const [invoiceType, setInvoiceType] = useState('invoice'); // invoice, bill, quotation, estimate
  const [invoiceNumber, setInvoiceNumber] = useState('INV-001');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  
  // Business Details
  const [businessDetails, setBusinessDetails] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    gstin: '',
    pan: '',
    logo: '',
  });

  // Customer Details
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    gstin: '',
  });

  // Items
  const [items, setItems] = useState([
    { id: 1, description: '', hsn: '', quantity: 1, rate: 0, gstRate: 18, amount: 0 }
  ]);

  // Additional Charges & Discounts
  const [discount, setDiscount] = useState({ type: 'percent', value: 0 });
  const [shippingCharge, setShippingCharge] = useState(0);
  const [packagingCharge, setPackagingCharge] = useState(0);

  // Bank Details for Payment
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountType: 'Current',
    upiId: '',
  });

  // Notes & Terms
  const [notes, setNotes] = useState('Thank you for your business!');
  const [terms, setTerms] = useState('1. Payment is due within 30 days.\n2. Please include invoice number on your payment.');

  // Calculated Values
  const [totals, setTotals] = useState({
    subtotal: 0,
    discountAmount: 0,
    taxableAmount: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    totalGst: 0,
    shippingCharge: 0,
    packagingCharge: 0,
    grandTotal: 0,
  });
  const [isCompactViewport, setIsCompactViewport] = useState(false);
  const pageRef = useRef(null);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsCompactViewport(window.innerWidth <= 768);
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);

    return () => {
      window.removeEventListener('resize', updateViewportMode);
    };
  }, []);

  useEffect(() => {
    applyMobileInputHints(pageRef.current);
  }, [items.length, businessDetails.phone, customerDetails.phone, shippingCharge, packagingCharge, discount]);

  // Calculate totals whenever items or charges change
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculateTotals();
  }, [items, discount, shippingCharge, packagingCharge, businessDetails.state, customerDetails.state]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculateTotals = () => {
    // Calculate subtotal
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
    
    // Calculate discount
    let discountAmount = 0;
    if (discount.type === 'percent') {
      discountAmount = (subtotal * discount.value) / 100;
    } else {
      discountAmount = discount.value;
    }
    
    const taxableAmount = subtotal - discountAmount;
    
    // Calculate GST for each item and sum up
    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;
    
    const isInterState = businessDetails.state && customerDetails.state && 
                         businessDetails.state !== customerDetails.state;
    
    items.forEach(item => {
      const itemTaxable = (item.quantity * item.rate) * (1 - discount.value / 100);
      const gstAmount = (itemTaxable * item.gstRate) / 100;
      
      if (isInterState) {
        totalIgst += gstAmount;
      } else {
        totalCgst += gstAmount / 2;
        totalSgst += gstAmount / 2;
      }
    });
    
    const totalGst = totalCgst + totalSgst + totalIgst;
    const shipping = Number(shippingCharge) || 0;
    const packaging = Number(packagingCharge) || 0;
    const grandTotal = taxableAmount + totalGst + shipping + packaging;
    
    setTotals({
      subtotal: Math.round(subtotal * 100) / 100,
      discountAmount: Math.round(discountAmount * 100) / 100,
      taxableAmount: Math.round(taxableAmount * 100) / 100,
      cgst: Math.round(totalCgst * 100) / 100,
      sgst: Math.round(totalSgst * 100) / 100,
      igst: Math.round(totalIgst * 100) / 100,
      totalGst: Math.round(totalGst * 100) / 100,
      shippingCharge: shipping,
      packagingCharge: packaging,
      grandTotal: Math.round(grandTotal * 100) / 100,
    });
  };

  const addItem = () => {
    const newId = Math.max(...items.map(i => i.id), 0) + 1;
    setItems([...items, { id: newId, description: '', hsn: '', quantity: 1, rate: 0, gstRate: 18, amount: 0 }]);
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        updated.amount = updated.quantity * updated.rate;
        return updated;
      }
      return item;
    }));
  };

  const handleDownloadPDF = () => {
    generateInvoicePDF({
      invoiceType,
      invoiceNumber,
      invoiceDate,
      dueDate,
      businessDetails,
      customerDetails,
      items,
      totals,
      discount,
      bankDetails,
      notes,
      terms,
    });
  };

  const handleReset = () => {
    setInvoiceNumber('INV-001');
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setDueDate('');
    setCustomerDetails({
      name: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', gstin: '',
    });
    setItems([{ id: 1, description: '', hsn: '', quantity: 1, rate: 0, gstRate: 18, amount: 0 }]);
    setDiscount({ type: 'percent', value: 0 });
    setShippingCharge(0);
    setPackagingCharge(0);
    setNotes('Thank you for your business!');
  };

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry',
    'Chandigarh', 'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli', 'Daman and Diu', 'Lakshadweep'
  ];

  const gstRates = [0, 0.25, 3, 5, 12, 18, 28];

  const invoiceTypeLabels = {
    invoice: 'Tax Invoice',
    bill: 'Bill of Supply',
    quotation: 'Quotation',
    estimate: 'Estimate',
    proforma: 'Proforma Invoice',
  };

  return (
    <div className="calculator-page fade-in" ref={pageRef}>
      <SEO 
        title="Invoice Generator"
        description="Free Invoice Generator India - Create GST compliant invoices with CGST/SGST. Download professional PDF invoices."
        keywords="invoice generator, GST invoice generator, invoice maker India, bill generator, invoice PDF download, CGST SGST invoice"
        canonicalPath="/invoice"
      />
      <div className="calculator-header">
        <h1>Invoice & Bill Generator</h1>
        <p>Create professional GST-compliant invoices, bills, and quotations</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: isCompactViewport ? '16px' : '24px' }}>
        {/* Left Column */}
        <div style={{ minWidth: 0 }}>
          {/* Invoice Type & Details */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: 'var(--surface)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaFileInvoice /> Invoice Details
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: '12px' }}>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Document Type</label>
                <select
                  value={invoiceType}
                  onChange={(e) => setInvoiceType(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                >
                  {Object.entries(invoiceTypeLabels).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Invoice Number</label>
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="INV-001"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Invoice Date</label>
                <input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Due Date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Business Details */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: '#eff6ff', borderRadius: '12px', border: '1px solid #3b82f6' }}>
            <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#2563eb' }}>
              <FaBuilding /> Your Business Details
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: '12px' }}>
              <div className="input-group" style={{ marginBottom: '0', gridColumn: isCompactViewport ? 'auto' : 'span 2' }}>
                <label>Business Name *</label>
                <input
                  type="text"
                  value={businessDetails.name}
                  onChange={(e) => setBusinessDetails({...businessDetails, name: e.target.value})}
                  placeholder="Your Business Name"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0', gridColumn: isCompactViewport ? 'auto' : 'span 2' }}>
                <label>Address</label>
                <input
                  type="text"
                  value={businessDetails.address}
                  onChange={(e) => setBusinessDetails({...businessDetails, address: e.target.value})}
                  placeholder="Street Address"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>City</label>
                <input
                  type="text"
                  value={businessDetails.city}
                  onChange={(e) => setBusinessDetails({...businessDetails, city: e.target.value})}
                  placeholder="City"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>State</label>
                <select
                  value={businessDetails.state}
                  onChange={(e) => setBusinessDetails({...businessDetails, state: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                >
                  <option value="">Select State</option>
                  {indianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Phone</label>
                <input
                  type="text"
                  value={businessDetails.phone}
                  onChange={(e) => setBusinessDetails({...businessDetails, phone: e.target.value})}
                  placeholder="Phone Number"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Email</label>
                <input
                  type="email"
                  value={businessDetails.email}
                  onChange={(e) => setBusinessDetails({...businessDetails, email: e.target.value})}
                  placeholder="Email"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>GSTIN</label>
                <input
                  type="text"
                  value={businessDetails.gstin}
                  onChange={(e) => setBusinessDetails({...businessDetails, gstin: e.target.value.toUpperCase()})}
                  placeholder="22AAAAA0000A1Z5"
                  maxLength={15}
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>PAN</label>
                <input
                  type="text"
                  value={businessDetails.pan}
                  onChange={(e) => setBusinessDetails({...businessDetails, pan: e.target.value.toUpperCase()})}
                  placeholder="ABCDE1234F"
                  maxLength={10}
                />
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: '#fef3c7', borderRadius: '12px', border: '1px solid #f59e0b' }}>
            <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#d97706' }}>
              <FaUser /> Customer / Bill To
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: '12px' }}>
              <div className="input-group" style={{ marginBottom: '0', gridColumn: isCompactViewport ? 'auto' : 'span 2' }}>
                <label>Customer Name *</label>
                <input
                  type="text"
                  value={customerDetails.name}
                  onChange={(e) => setCustomerDetails({...customerDetails, name: e.target.value})}
                  placeholder="Customer Name"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0', gridColumn: isCompactViewport ? 'auto' : 'span 2' }}>
                <label>Address</label>
                <input
                  type="text"
                  value={customerDetails.address}
                  onChange={(e) => setCustomerDetails({...customerDetails, address: e.target.value})}
                  placeholder="Street Address"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>City</label>
                <input
                  type="text"
                  value={customerDetails.city}
                  onChange={(e) => setCustomerDetails({...customerDetails, city: e.target.value})}
                  placeholder="City"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>State</label>
                <select
                  value={customerDetails.state}
                  onChange={(e) => setCustomerDetails({...customerDetails, state: e.target.value})}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                >
                  <option value="">Select State</option>
                  {indianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Phone</label>
                <input
                  type="text"
                  value={customerDetails.phone}
                  onChange={(e) => setCustomerDetails({...customerDetails, phone: e.target.value})}
                  placeholder="Phone Number"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>GSTIN (if applicable)</label>
                <input
                  type="text"
                  value={customerDetails.gstin}
                  onChange={(e) => setCustomerDetails({...customerDetails, gstin: e.target.value.toUpperCase()})}
                  placeholder="Customer GSTIN"
                  maxLength={15}
                />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #22c55e' }}>
            <h3 style={{ marginBottom: '16px', color: '#16a34a' }}>🏦 Bank Details (for Payment)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: '12px' }}>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Bank Name</label>
                <input
                  type="text"
                  value={bankDetails.bankName}
                  onChange={(e) => setBankDetails({...bankDetails, bankName: e.target.value})}
                  placeholder="Bank Name"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Account Number</label>
                <input
                  type="text"
                  value={bankDetails.accountNumber}
                  onChange={(e) => setBankDetails({...bankDetails, accountNumber: e.target.value})}
                  placeholder="Account Number"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>IFSC Code</label>
                <input
                  type="text"
                  value={bankDetails.ifscCode}
                  onChange={(e) => setBankDetails({...bankDetails, ifscCode: e.target.value.toUpperCase()})}
                  placeholder="IFSC Code"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>UPI ID</label>
                <input
                  type="text"
                  value={bankDetails.upiId}
                  onChange={(e) => setBankDetails({...bankDetails, upiId: e.target.value})}
                  placeholder="yourname@upi"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div style={{ minWidth: 0 }}>
          {/* Items Section */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: 'var(--surface)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <h3 style={{ marginBottom: '16px' }}>📦 Items / Services</h3>
            
            <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', minWidth: isCompactViewport ? '640px' : '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>#</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', minWidth: '150px' }}>Description</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>HSN/SAC</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Qty</th>
                    <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Rate</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>GST%</th>
                    <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Amount</th>
                    <th style={{ padding: '10px', borderBottom: '2px solid #e2e8f0' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={item.id}>
                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>{index + 1}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                          placeholder="Item description"
                          style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                        />
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                        <input
                          type="text"
                          value={item.hsn}
                          onChange={(e) => updateItem(item.id, 'hsn', e.target.value)}
                          placeholder="HSN"
                          style={{ width: '70px', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                        />
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, 'quantity', Number(e.target.value))}
                          min="1"
                          style={{ width: '60px', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', textAlign: 'center' }}
                        />
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                        <input
                          type="number"
                          value={item.rate}
                          onChange={(e) => updateItem(item.id, 'rate', Number(e.target.value))}
                          min="0"
                          style={{ width: '80px', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px', textAlign: 'right' }}
                        />
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                        <select
                          value={item.gstRate}
                          onChange={(e) => updateItem(item.id, 'gstRate', Number(e.target.value))}
                          style={{ width: '70px', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                        >
                          {gstRates.map(rate => (
                            <option key={rate} value={rate}>{rate}%</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', fontWeight: 'bold' }}>
                        {formatCurrency(item.quantity * item.rate)}
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #e2e8f0' }}>
                        <button
                          onClick={() => removeItem(item.id)}
                          disabled={items.length === 1}
                          style={{ 
                            padding: '6px 10px', 
                            background: items.length === 1 ? '#e2e8f0' : '#ef4444', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '4px', 
                            cursor: items.length === 1 ? 'not-allowed' : 'pointer' 
                          }}
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <button 
              onClick={addItem}
              style={{ 
                marginTop: '12px', 
                width: '100%', 
                padding: '10px', 
                background: '#3b82f6', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <FaPlus /> Add Item
            </button>
          </div>

          {/* Discount & Charges */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: '#faf5ff', borderRadius: '12px', border: '1px solid #a855f7' }}>
            <h3 style={{ marginBottom: '16px', color: '#7c3aed' }}>💸 Discount & Additional Charges</h3>
            <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr 1fr', gap: '12px' }}>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Discount</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    value={discount.value}
                    onChange={(e) => setDiscount({...discount, value: Number(e.target.value)})}
                    min="0"
                    style={{ flex: 1, padding: '8px', border: '1px solid var(--border)', borderRadius: '4px' }}
                  />
                  <select
                    value={discount.type}
                    onChange={(e) => setDiscount({...discount, type: e.target.value})}
                    style={{ padding: '8px', border: '1px solid var(--border)', borderRadius: '4px' }}
                  >
                    <option value="percent">%</option>
                    <option value="flat">₹</option>
                  </select>
                </div>
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Shipping (₹)</label>
                <input
                  type="number"
                  value={shippingCharge === '' ? '' : shippingCharge}
                  onChange={(e) => setShippingCharge(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Packaging (₹)</label>
                <input
                  type="number"
                  value={packagingCharge === '' ? '' : packagingCharge}
                  onChange={(e) => setPackagingCharge(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Totals */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: '12px', color: 'white' }}>
            <h3 style={{ marginBottom: '16px' }}>📊 Invoice Summary</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Subtotal:</span>
                <span>{formatCurrency(totals.subtotal)}</span>
              </div>
              {totals.discountAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fef3c7' }}>
                  <span>Discount:</span>
                  <span>-{formatCurrency(totals.discountAmount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Taxable Amount:</span>
                <span>{formatCurrency(totals.taxableAmount)}</span>
              </div>
              {totals.cgst > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', opacity: 0.9 }}>
                  <span>CGST:</span>
                  <span>{formatCurrency(totals.cgst)}</span>
                </div>
              )}
              {totals.sgst > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', opacity: 0.9 }}>
                  <span>SGST:</span>
                  <span>{formatCurrency(totals.sgst)}</span>
                </div>
              )}
              {totals.igst > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', opacity: 0.9 }}>
                  <span>IGST:</span>
                  <span>{formatCurrency(totals.igst)}</span>
                </div>
              )}
              {shippingCharge > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', opacity: 0.9 }}>
                  <span>Shipping:</span>
                  <span>{formatCurrency(shippingCharge)}</span>
                </div>
              )}
              {packagingCharge > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', opacity: 0.9 }}>
                  <span>Packaging:</span>
                  <span>{formatCurrency(packagingCharge)}</span>
                </div>
              )}
              <div style={{ borderTop: '2px solid rgba(255,255,255,0.3)', marginTop: '8px', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: 'bold' }}>
                <span>Grand Total:</span>
                <span>{formatCurrency(totals.grandTotal)}</span>
              </div>
            </div>
          </div>

          {/* Notes & Terms */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: 'var(--surface)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <h3 style={{ marginBottom: '16px' }}>📝 Notes & Terms</h3>
            <div className="input-group">
              <label>Notes (shown on invoice)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="2"
                style={{ width: '100%', padding: '10px', border: '1px solid var(--border)', borderRadius: '8px', resize: 'vertical' }}
              />
            </div>
            <div className="input-group" style={{ marginBottom: '0' }}>
              <label>Terms & Conditions</label>
              <textarea
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                rows="3"
                style={{ width: '100%', padding: '10px', border: '1px solid var(--border)', borderRadius: '8px', resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="button-group" style={{ display: 'flex', flexDirection: isCompactViewport ? 'column' : 'row', gap: '12px' }}>
            <button className="btn btn-outline" onClick={handleReset} style={{ flex: 1 }}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={handleDownloadPDF}
              style={{ flex: 2, background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}
            >
              <FaDownload style={{ marginRight: '8px' }} /> Download {invoiceTypeLabels[invoiceType]}
            </button>
          </div>
        </div>
      </div>

      <div className="info-box" style={{ marginTop: '24px' }}>
        <h4>📌 GST Invoice Guidelines</h4>
        <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <strong>When to use CGST + SGST:</strong>
            <p style={{ marginTop: '4px', fontSize: '14px', color: 'var(--text-secondary)' }}>
              When buyer and seller are in the same state (Intra-state supply)
            </p>
          </div>
          <div>
            <strong>When to use IGST:</strong>
            <p style={{ marginTop: '4px', fontSize: '14px', color: 'var(--text-secondary)' }}>
              When buyer and seller are in different states (Inter-state supply)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceGenerator;
